// Redux
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
// Components
import Pagination from '../Pagination';
import SummarySearch from '../SummarySearch';
import CardNewsSearched from '../CardNewsSearched';
import { LoadingSkeleton } from '../../Components/LoadingSkeleton';
import { SkeletonTypes } from '../../Components/LoadingSkeleton/types';
// Slices
import {
	setPageFetched,
	setNews,
	setTotalNewsQuery,
	setQueryNewsView,
	setPageShowed,
} from '../../app/features/newsSlice';
// Hooks
import { useScrollToTop } from '../../hooks/useScrollToTop';
// Services
import { getNewsFromQuery } from '../../services/getNewsFromQuery';
// Icons
import { IoMdCloseCircle } from 'react-icons/io';
// Style
import './NewsQuerySection.css';
import { FavoriteCategory } from '../../Views/FavoriteDetail/types';
import { useAuth } from '../../hooks/useAuth';
// Translate
import { useTranslation } from 'react-i18next';
import { filterBlockedNewsItems } from '../../utils/newsFilters';

const NewsQuerySection = ({ language }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { isAuth } = useAuth();
	const [isPageChanging, setIsPageChanging] = useState(false);
	// Hooks
	const { handleScrollToTop } = useScrollToTop();

	// Variables
	const { queryFetched } = useSelector((state) => state.library);
	const { news, total_news_query, pageFetched, is_news_loading, pageShowed } =
		useSelector((state) => state.news);

	// Pagination
	const itemsPerPage = 10;
	const indexOfLastItem = pageShowed * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = news.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(total_news_query / itemsPerPage);

	const handlePageChange = async (newPage) => {
		handleScrollToTop(0);
		if (newPage > pageShowed) {
			setIsPageChanging(true);

			const maxApiPages = Math.ceil(total_news_query / itemsPerPage);
			let nextFetchedPage = pageFetched;
			let mergedNews = [...news];
			while (mergedNews.length < newPage * itemsPerPage && nextFetchedPage < maxApiPages) {
				try {
					nextFetchedPage += 1;
					const response = await getNewsFromQuery(
						queryFetched,
						nextFetchedPage,
					);
					const filteredNewItems = filterBlockedNewsItems(
						response?.data?.data || [],
					);
					mergedNews = [...mergedNews, ...filteredNewItems];
				} catch (error) {
					console.error('Request error:', error);
					break;
				}
			}

			if (nextFetchedPage !== pageFetched || mergedNews.length !== news.length) {
				dispatch(setPageFetched(nextFetchedPage));
				dispatch(setNews(mergedNews));
			}

			if (nextFetchedPage >= maxApiPages) {
				const realTotal = mergedNews.length;
				const realTotalPages = Math.max(1, Math.ceil(realTotal / itemsPerPage));
				if (realTotal !== total_news_query) {
					dispatch(setTotalNewsQuery(realTotal));
				}
				if (newPage > realTotalPages) {
					dispatch(setPageShowed(realTotalPages));
					setIsPageChanging(false);
					return;
				}
			}
			dispatch(setPageShowed(newPage));
			setIsPageChanging(false);
		} else {
			if (pageShowed > 1) {
				dispatch(setPageShowed(newPage));
			}
		}
	};

	return (
		<>
			{is_news_loading && (
				<section className='newsQueryResults loading'>
					{Array.from({ length: 10 }).map((_, i) => (
						<LoadingSkeleton key={i} type={SkeletonTypes.LIBRARY_CARD} />
					))}
				</section>
			)}

			{!is_news_loading && news?.length > 0 && (
				<div className='newsQueryContent'>
					<div className='newsQueryContentInfo'>
						<SummarySearch total={total_news_query} />
						<button onClick={() => dispatch(setQueryNewsView(false))}>
							<IoMdCloseCircle className='newsQueryContentIcon' />
						</button>
					</div>

					<section className='newsQueryResults'>
						{isPageChanging
							? Array.from({ length: 10 }).map((_, i) => (
									<LoadingSkeleton key={i} type={SkeletonTypes.LIBRARY_CARD} />
							  ))
							: currentItems?.map((item, i) => (
									<CardNewsSearched
										key={i}
										title={item.title}
										description={item.description}
										url={item.url}
										image_url={item.image_url}
										publication_date={item.publication_date}
										resource_origin={item.source}
										favoriteCategory={FavoriteCategory.NEWS}
										showFavoriteButton={isAuth()}
										language={language}
									/>
							  ))}
					</section>

					<Pagination
						pageShowed={pageShowed}
						totalPages={totalPages}
						handlePageChange={handlePageChange}
					/>
				</div>
			)}

			{!is_news_loading && (!news || news.length === 0) && (
				<main className='noDataContainerNews'>
					<p>{t('No data found')}</p>
				</main>
			)}
		</>
	);
};

export default NewsQuerySection;
